import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { NgIcon } from '@ng-icons/core';
import { injectOrg, injectUser } from '@quipex/core/auth';
import { injectSupportsHoverInteraction } from '@quipex/core/utils';
import {
    ClickOutside,
    NavHeroItem,
    QpxButton,
    QpxIcon,
    QpxNavDirectives,
    type NavItem,
    type NavListTemplateContext,
    getAvatarColorClasses,
    QpxTooltip,
} from '@quipex/shared/ui';
import { UiState } from './ui-state';

@Component({
    selector: 'qpx-sidebar',
    imports: [
        NgTemplateOutlet,
        RouterLink,
        RouterLinkActive,
        MatBadgeModule,
        MatButtonModule,
        QpxButton,
        NgIcon,
        MatListModule,
        QpxIcon,
        QpxNavDirectives,
        ClickOutside,
        NavHeroItem,
        QpxTooltip,
    ],
    templateUrl: './sidebar.html',
    styleUrls: ['./sidebar.scss'],
    providers: [UiState],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'sidebar',
        '[class.desktop]': 'isRailOrWider()',
        '[class.mobile]': 'isOffCanvas()',
        '[class.expanded]': 'isEffectiveExpanded()',
        '(mouseenter)': 'onMouseEnter()',
        '(mouseleave)': 'onMouseLeave($event)',
    },
})
export class Sidebar {
    //#region Dependency injection
    private readonly _me = injectUser();
    private readonly _org = injectOrg();
    private readonly destroyRef = inject(DestroyRef);
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly uiState = inject(UiState);
    private readonly supportsHoverInteraction =
        injectSupportsHoverInteraction();
    //#endregion

    //#region Constants
    protected readonly clickOutsideIgnoredSelectors = [
        '.cdk-overlay-container',
        '.menu-toggle',
    ] as const;
    protected readonly contactUsHref = this.uiState.contactUsHref;
    //#endregion

    //#region Inputs
    public readonly hasUnreadReleaseNotes = input(false);
    public readonly isExpanded = input.required<boolean>();
    public readonly isHoverExpanded = input(false);
    public readonly extraGeneralNavItems = input<readonly NavItem[]>([]);
    //#endregion

    //#region Outputs
    public readonly toggleChange = output<boolean>();
    public readonly hoveredChange = output<boolean>();
    public readonly findBuildingRequested = output<void>();
    public readonly focusRestoreRequested = output<void>();
    public readonly whatsNewRequested = output<void>();
    //#endregion

    //#region User state
    protected readonly org = this._org.required;
    protected readonly me = this._me.required;
    protected readonly userInitials = this._me.initials;
    protected readonly avatarClasses = computed(() => {
        const u = this.me();
        const classes = getAvatarColorClasses(u.id);
        return `initials ${classes.bg} ${classes.text}`;
    });
    //#endregion

    //#region State
    protected readonly inAppHelpSupportAvailable =
        this.uiState.inAppHelpSupportAvailable;
    protected readonly isRailOrWider = this.uiState.isRailOrWider;
    protected readonly isOffCanvas = this.uiState.isOffCanvas;

    protected readonly isEffectiveExpanded = computed(() =>
        this.isRailOrWider()
            ? this.isExpanded() || this.isHoverExpanded()
            : this.isExpanded()
    );
    protected readonly isProd = this.uiState.isProd;
    protected readonly isDemo = this.uiState.isDemo;
    protected readonly portalContent = this.uiState.portalContent;
    protected readonly hasPortalContent = this.uiState.hasPortalContent;
    protected readonly resolvedNavItemsGeneral = computed<NavItem[]>(() =>
        this.uiState
            .resolvedNavItemsGeneral()
            .concat(this.extraGeneralNavItems())
    );
    protected readonly resolvedNavItemsOrg = this.uiState.resolvedNavItemsOrg;
    //#endregion

    //#region Lifecycle hooks
    public constructor() {
        this.uiState.findBuildingRequested$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.findBuildingRequested.emit());
    }
    //#endregion

    //#region Public methods
    public focusFirstControl(): void {
        this.elementRef.nativeElement
            .querySelector<HTMLElement>(
                '.sidebar-inner a[href], .sidebar-inner button:not([disabled]), .sidebar-inner [tabindex]:not([tabindex="-1"])'
            )
            ?.focus();
    }
    //#endregion

    //#region Template methods
    protected getNavItemsTemplateContext(): NavListTemplateContext {
        return {
            isEffectiveExpanded: () => this.isEffectiveExpanded(),
            onNavItemClick: (item: NavItem) => this.onNavItemClick(item),
        };
    }

    protected onNavItemClick(item: NavItem): void {
        // Execute specific click if provided
        if (item.kind === 'link' || item.kind === 'button') {
            item.onClick?.();
        }

        // Close on mobile navigation
        if (this.isOffCanvas()) {
            this.closeSidemenu();
        }
    }

    protected closeSidemenu(): void {
        if (this.isRailOrWider()) return;

        this.toggleChange.emit(false);
    }

    protected requestWhatsNew(): void {
        this.whatsNewRequested.emit();
    }

    /** Close sidemenu on mobile */
    protected onClickedOutside(): void {
        if (this.isRailOrWider()) return;

        if (this.isExpanded()) {
            this.toggleChange.emit(false);
            this.focusRestoreRequested.emit();
        }
    }

    protected onEscape(event: Event): void {
        if (this.isRailOrWider() || !this.isExpanded()) return;

        event.preventDefault();
        event.stopPropagation();
        this.toggleChange.emit(false);
        this.focusRestoreRequested.emit();
    }

    protected onMouseEnter(): void {
        if (!this.supportsHoverInteraction()) return;

        if (this.isRailOrWider()) {
            this.hoveredChange.emit(true);
        }
    }

    protected onMouseLeave(event: MouseEvent): void {
        if (!this.supportsHoverInteraction()) return;
        if (isWithin(event.relatedTarget, '[data-layout-header]')) return;

        if (this.isRailOrWider()) {
            this.hoveredChange.emit(false);
        }
    }

    protected signout(): void {
        this.uiState.signout();
    }
    //#endregion
}

function isWithin(target: EventTarget | null, selector: string): boolean {
    return target instanceof Element && target.closest(selector) !== null;
}
